import React, { FunctionComponent, useCallback, useState } from 'react';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import WideContent from 'core/layout/content/wide-content';
import DocumentsTable from './documents-table';
import { InvestorProductDocument, ProductPermissionEnum } from 'src/api';
import DeleteDocumentConfirmationModal from 'apps/issuer/pages/documents/delete-document-confirmation-modal';
import DeleteDocumentSuccessfulModal from 'apps/issuer/pages/documents/delete-document-successful-modal';
import Button from 'src/ui/atoms/button';
import AddDocumentsModal from './add-documents-modal';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import Spacer from 'src/ui/atoms/spacer';
import { hasDistributionViewDataPermission } from '../../helpers/distributionPlatformPermissions';
import { hasViewPermission } from '../../helpers/tokenPermissions';
import PageWrapper from 'src/core/layout/content/page-wrapper';

const IssuerDocumentsPage: FunctionComponent = () => {
  const [documentForDeletionModalOpen, setDocumentForDeletionModalOpen] = useState<InvestorProductDocument>();
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState(false);
  const [isAddDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  const { currentUser } = useCurrentUserSelector();
  const hasManageDocumentsPermission = currentUser?.permissionOnAtLeastOneProduct.includes(
    ProductPermissionEnum.MANAGE_INVESTOR_DOCUMENTS,
  );
  const hasPermission =
    hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
    hasViewPermission(currentUser);

  const onDeleteDocument = useCallback(() => {
    setDocumentForDeletionModalOpen(undefined);
    setConfirmDeletionModalOpen(true);
    refreshTableData();
  }, []);

  const onAddNewDocument = useCallback(() => {
    setAddDocumentModalOpen(false);
    refreshTableData();
  }, [forceDataLoad]);

  const refreshTableData = useCallback(() => {
    setForceDataLoad(true);
  }, []);

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent title={<Translate name="dashboard.issuerDocuments" />}>
        {hasPermission && hasManageDocumentsPermission && (
          <>
            <Button
              variant="primary"
              icon="plus"
              size="small"
              iconSize="small"
              iconColor="inherit"
              onClick={() => setAddDocumentModalOpen(true)}
            >
              <Translate name="addDocuments.title.default" />
            </Button>
            <Spacer y={4} />
          </>
        )}
        <DocumentsTable
          hasPermission={hasPermission}
          forceDataLoad={forceDataLoad}
          setForceDataLoad={setForceDataLoad}
          onDeleteButtonClick={(document: InvestorProductDocument) => setDocumentForDeletionModalOpen(document)}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        {!!documentForDeletionModalOpen && (
          <DeleteDocumentConfirmationModal
            onDelete={onDeleteDocument}
            onModalClose={() => setDocumentForDeletionModalOpen(undefined)}
            document={documentForDeletionModalOpen}
          />
        )}
        {confirmDeletionModalOpen && (
          <DeleteDocumentSuccessfulModal onModalClose={() => setConfirmDeletionModalOpen(false)} />
        )}

        {isAddDocumentModalOpen && (
          <AddDocumentsModal onClose={() => setAddDocumentModalOpen(false)} onAddNewDocument={onAddNewDocument} />
        )}
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerDocumentsPage, AppType.ISSUER);
